import {
  AccountCenter,
  type AiNameAnalysisState,
  AnalyticsConsent,
  ApiRequestError,
  AuthPage,
  AuthPanel,
  analyzeName,
  ComplianceReportForm,
  createGeneration,
  downloadNameCard,
  type GenerationRequest,
  getLegalDocument,
  LEGAL_PATHS,
  LegalDocumentContent,
  type LegalPath,
  loadFigure,
  loadGeneration,
  loadMe,
  loadNameAnalysis,
  loadSiteConfig,
  loadSurname,
  loadSurnames,
  type MeResponse,
  type NameAnalysis,
  NameAnalysisEnhancement,
  openConsentSettings,
  PurchaseModal,
  ResetPasswordPanel,
  type ResultName,
  regenerate,
  type SiteConfigResponse,
  type SurnameFigure,
  type SurnameRecord,
  TurnstileWidget,
  toggleFavorite,
  trackEvent,
  useCountUp,
  useInView,
  usePointerParallax,
} from "@jieyi/web-shared";
import {
  ArrowRight,
  BookOpenText,
  Check,
  CircleAlert,
  Clock3,
  Download,
  Feather,
  Heart,
  Languages,
  LoaderCircle,
  RefreshCw,
  ScrollText,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
} from "lucide-react";
import { type CSSProperties, type FormEvent, useCallback, useEffect, useState } from "react";

function Header() {
  return (
    <header className="site-header">
      <a className="brand" href="/">
        <span className="seal">结</span>
        <span>
          结一<small>认真取名</small>
        </span>
      </a>
      <nav>
        <a href="/#naming">开始取名</a>
        <a href="/#method">取名方法</a>
        <a href="/surnames">百家姓</a>
        <a href="/meaning">名字解析</a>
        <a href="/pricing">价格</a>
        <a className="language-link" href="https://en.jieyiai.dev">
          <Languages size={15} />
          English
        </a>
      </nav>
      <AuthPanel language="zh" compact />
    </header>
  );
}

function Footer() {
  return (
    <footer>
      <div>
        <span className="seal small">结</span>
        <strong>结一</strong>
        <p>名字不是一句漂亮话，而是一份会被使用很多年的礼物。</p>
      </div>
      <div className="footer-links">
        <a href="/terms">服务条款</a>
        <a href="/privacy">隐私政策</a>
        <a href="/acceptable-use">AIGC 规范</a>
        <a href="/ai-disclosure">AI 模型</a>
        <a href="/refund">退款</a>
        <a href="/cookies">Cookie 政策</a>
        <button className="text-button" type="button" onClick={openConsentSettings}>
          隐私设置
        </button>
        <a href="/report">联系与举报</a>
      </div>
      <p className="copyright">© 2026 Jieyi AI · AI 生成建议仅供参考</p>
      <AnalyticsConsent language="zh" />
    </footer>
  );
}

function LegalPage({ path }: { path: LegalPath }) {
  const document = getLegalDocument("zh", path);
  return (
    <>
      <Header />
      <main className="legal-page">
        <LegalDocumentContent document={document} />
        <a className="primary-button" href="/">
          返回首页
        </a>
      </main>
      <Footer />
    </>
  );
}

function ReportPage() {
  return (
    <>
      <Header />
      <main className="legal-page">
        <ComplianceReportForm language="zh" />
      </main>
      <Footer />
    </>
  );
}

function PricingPage() {
  const [cardRef, cardInView] = useInView<HTMLDivElement>();
  const [config, setConfig] = useState<SiteConfigResponse | null>(null);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const refreshMe = useCallback(async () => setMe(await loadMe()), []);
  const closePurchase = useCallback(() => setPurchaseOpen(false), []);
  useEffect(() => {
    void Promise.all([loadSiteConfig(), loadMe()]).then(([siteConfig, account]) => {
      setConfig(siteConfig);
      setMe(account);
    });
  }, []);
  const openPurchase = () => {
    if (!me?.user) {
      window.location.href = "/login";
      return;
    }
    setPurchaseOpen(true);
  };
  return (
    <>
      <Header />
      <main className="pricing-page">
        <div
          className={cardInView ? "pricing-card reveal in-view" : "pricing-card reveal"}
          ref={cardRef}
        >
          <span className="eyebrow">简单透明</span>
          <h1>不订阅，只为满意的一次付费</h1>
          <div className="price">
            <b>¥9.90</b>
            <span>/ 次</span>
          </div>
          <p className="pricing-pack-note">十次 ¥89 · 平均 ¥8.90/次 · 节省 ¥10</p>
          <ul>
            <li>
              <Check />5 个完整候选姓名
            </li>
            <li>
              <Check />
              真实诗词出处与逐字解释
            </li>
            <li>
              <Check />
              音律、字形与避坑分析
            </li>
            <li>
              <Check />
              生成失败自动返还次数
            </li>
          </ul>
          <p className="model-disclosure">
            AI 模型：DeepSeek V4 Flash；失败重试可能使用 DeepSeek V4 Pro。
            <a href="/ai-disclosure">查看模型与安全说明</a>
          </p>
          <button className="primary-button" type="button" onClick={openPurchase}>
            选择次数包
            <ArrowRight />
          </button>
        </div>
      </main>
      <Footer />
      <PurchaseModal
        language="zh"
        open={purchaseOpen}
        products={config?.products ?? []}
        paymentEnabled={config?.features.payment ?? false}
        onClose={closePurchase}
        onRefresh={refreshMe}
      />
    </>
  );
}

function SurnamePage() {
  const [items, setItems] = useState<SurnameRecord[]>([]);
  const [query, setQuery] = useState("");
  useEffect(() => {
    void loadSurnames().then(setItems);
  }, []);
  async function search(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setItems(await loadSurnames(query));
  }
  return (
    <>
      <Header />
      <main className="directory-page">
        <span className="eyebrow">CHINESE SURNAMES</span>
        <h1>百家姓，不止一个排名。</h1>
        <p>收录 300 余个姓氏，点击任意姓氏查看渊源、名人与分布。</p>
        <form className="directory-search" onSubmit={search}>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="输入姓氏或拼音"
          />
          <button className="primary-button" type="submit">
            查找
          </button>
        </form>
        <div className="surname-grid">
          {items.map((item) => (
            <a
              className="surname-card"
              href={`/surnames/${encodeURIComponent(item.hanzi)}`}
              key={item.hanzi}
            >
              <b>{item.hanzi}</b>
              <div>
                <h2>{item.pinyin}</h2>
                <p>{item.meaningZh}</p>
                <small>{item.rank ? `常见姓氏参考排名 #${item.rank}` : "传统中文姓氏"}</small>
              </div>
              <span className="surname-more">详情 →</span>
            </a>
          ))}
        </div>
      </main>
      <Footer />
    </>
  );
}

function SurnameDetailPage({ hanzi }: { hanzi: string }) {
  const [surname, setSurname] = useState<SurnameRecord | null>(null);
  const [error, setError] = useState("");
  const valid = /^[\u3400-\u9fff]{1,2}$/.test(hanzi);
  const [bodyRef, bodyInView] = useInView<HTMLDivElement>();
  useEffect(() => {
    if (!valid) {
      setError("姓氏格式无效");
      return;
    }
    void loadSurname(hanzi)
      .then(setSurname)
      .catch(() => setError("没有找到这个姓氏"));
  }, [hanzi, valid]);
  return (
    <>
      <Header />
      <main className="surname-detail">
        {error ? (
          <div className="surname-detail-error">
            <h1>{error}</h1>
            <a className="secondary-button" href="/surnames">
              返回百家姓
            </a>
          </div>
        ) : !surname ? (
          <div className="surname-detail-loading">正在查阅姓氏档案…</div>
        ) : (
          <div
            className={bodyInView ? "surname-body reveal in-view" : "surname-body reveal"}
            ref={bodyRef}
          >
            <header className="surname-hero">
              <div className="surname-hero-main">
                <span className="eyebrow">百家姓 · 姓氏档案</span>
                <b className="surname-hero-glyph">{surname.hanzi}</b>
                <div className="surname-hero-meta">
                  <h1>{surname.pinyin}</h1>
                  {surname.rank ? (
                    <span className="surname-rank">常见度 #{surname.rank}</span>
                  ) : null}
                </div>
              </div>
              <p className="surname-meaning">{surname.meaningZh}</p>
            </header>
            <div className="surname-sections">
              <section>
                <h2>渊源</h2>
                <p>{surname.originZh ?? "渊源资料整理中，敬请期待。"}</p>
              </section>
              {surname.storyZh ? (
                <section className="story">
                  <h2>典故</h2>
                  <p>{surname.storyZh}</p>
                </section>
              ) : null}
              {surname.figures?.length ? (
                <section>
                  <h2>历史名人</h2>
                  <div className="figure-chips">
                    {surname.figures.map((figure) => (
                      <a
                        className="figure-chip"
                        href={`/figures/${encodeURIComponent(figure.name)}`}
                        key={figure.name}
                      >
                        <b>{figure.name}</b>
                        {figure.titleZh ? <i>{figure.titleZh}</i> : null}
                      </a>
                    ))}
                  </div>
                </section>
              ) : (
                <section>
                  <h2>历史名人</h2>
                  <p>{surname.famousZh ?? "暂无名人资料。"}</p>
                </section>
              )}
              {surname.distributionZh ? (
                <section>
                  <h2>迁徙分布</h2>
                  <p>{surname.distributionZh}</p>
                </section>
              ) : null}
            </div>
            <div className="surname-actions">
              <a className="primary-button" href="/#naming">
                用「{surname.hanzi}」姓取个名字
              </a>
              <a className="secondary-button" href="/surnames">
                返回百家姓
              </a>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}

function FigurePage({ name }: { name: string }) {
  const [data, setData] = useState<{
    figure: SurnameFigure;
    surname: SurnameRecord;
    siblings: SurnameFigure[];
  } | null>(null);
  const [error, setError] = useState("");
  const valid = /^[\u3400-\u9fff]{2,4}$/.test(name);
  const [bodyRef, bodyInView] = useInView<HTMLDivElement>();
  useEffect(() => {
    if (!valid) {
      setError("人名格式无效");
      return;
    }
    void loadFigure(name)
      .then(setData)
      .catch(() => setError("没有找到这位名人"));
  }, [name, valid]);
  return (
    <>
      <Header />
      <main className="surname-detail figure-detail">
        {error ? (
          <div className="surname-detail-error">
            <h1>{error}</h1>
            <a className="secondary-button" href="/surnames">
              返回百家姓
            </a>
          </div>
        ) : !data ? (
          <div className="surname-detail-loading">正在查阅人物志…</div>
        ) : (
          <div
            className={bodyInView ? "surname-body reveal in-view" : "surname-body reveal"}
            ref={bodyRef}
          >
            <header className="surname-hero">
              <div className="surname-hero-main">
                <span className="eyebrow">人物志</span>
                <b className="surname-hero-glyph">{data.figure.name}</b>
                <div className="surname-hero-meta">
                  {data.figure.nameEn ? <h1>{data.figure.nameEn}</h1> : null}
                  <div className="figure-tags">
                    {data.figure.titleZh ? (
                      <span className="figure-title">{data.figure.titleZh}</span>
                    ) : null}
                    {data.figure.era ? <span className="figure-era">{data.figure.era}</span> : null}
                    <a
                      className="figure-surname-link"
                      href={`/surnames/${encodeURIComponent(data.figure.surnameHanzi)}`}
                    >
                      {data.figure.surnameHanzi}姓档案 →
                    </a>
                  </div>
                </div>
              </div>
              {data.figure.introZh ? (
                <p className="surname-meaning">{data.figure.introZh}</p>
              ) : (
                <p className="surname-meaning">
                  {data.figure.titleZh
                    ? `${data.figure.surnameHanzi}姓代表人物，${data.figure.titleZh}。`
                    : "生平资料整理中，敬请期待。"}
                </p>
              )}
            </header>
            {data.siblings?.length ? (
              <div className="surname-sections">
                <section>
                  <h2>{data.figure.surnameHanzi}姓其他名人</h2>
                  <div className="figure-chips">
                    {data.siblings.map((sibling) => (
                      <a
                        className="figure-chip"
                        href={`/figures/${encodeURIComponent(sibling.name)}`}
                        key={sibling.name}
                      >
                        <b>{sibling.name}</b>
                        {sibling.titleZh ? <i>{sibling.titleZh}</i> : null}
                      </a>
                    ))}
                  </div>
                </section>
              </div>
            ) : null}
            <div className="surname-actions">
              <a className="primary-button" href="/#naming">
                以「{data.figure.surnameHanzi}」为姓取个名字
              </a>
              <a
                className="secondary-button"
                href={`/surnames/${encodeURIComponent(data.figure.surnameHanzi)}`}
              >
                查看{data.figure.surnameHanzi}姓档案
              </a>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}

function MeaningPage() {
  const [name, setName] = useState("");
  const [analysis, setAnalysis] = useState<NameAnalysis | null>(null);
  const [ai, setAi] = useState<AiNameAnalysisState | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function pollAi(requestId: string) {
    for (let count = 0; count < 60; count += 1) {
      const current = await loadNameAnalysis(requestId);
      setAi(current);
      if (current.status === "completed" || current.status === "failed") return;
      await new Promise((resolve) => window.setTimeout(resolve, 2000));
    }
    setAi({ status: "failed", error: "解析超时" });
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      const response = await analyzeName(name);
      setAnalysis(response.analysis);
      setAi(response.ai);
      if (response.ai.status === "queued" && response.ai.requestId)
        await pollAi(response.ai.requestId);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "解析失败");
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Header />
      <main className="meaning-page">
        <div className="meaning-layout">
          <div className="meaning-intro">
            <span className="eyebrow">NAME MEANING</span>
            <h1>
              看看这个名字，
              <br />
              真正说了什么。
            </h1>
            <p className="meaning-lede">
              不编造出处，只做逐字拆解：每个字的本义与寓意、可回查的典籍原句、以及音形日用的避坑提示。
            </p>
            <form className="directory-search" onSubmit={submit}>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                maxLength={4}
                placeholder="输入 2—4 个汉字姓名"
                required
              />
              <button className="primary-button" disabled={busy} type="submit">
                {busy ? "解析中…" : "开始解析"}
              </button>
            </form>
            {error && <p className="form-error">{error}</p>}
            <ul className="meaning-points">
              <li>
                <BookOpenText />
                逐字含义与寓意拆解
              </li>
              <li>
                <ScrollText />
                已核验的典籍原句出处
              </li>
              <li>
                <ShieldCheck />
                生僻、谐音与日用风险提示
              </li>
            </ul>
          </div>
          <div className="meaning-panel">
            {!analysis && (
              <aside className="meaning-sample" aria-hidden="true">
                <div className="meaning-sample-head">
                  <span>解析结果示例</span>
                  <small>在左侧输入姓名，立即查看</small>
                </div>
                <h2>林晚晴</h2>
                <p>姓氏：林 · 名字：晚晴</p>
                <div className="character-grid">
                  <div>
                    <b>晚</b>
                    <span>日落之时，从容不迫</span>
                  </div>
                  <div>
                    <b>晴</b>
                    <span>雨过天晴，开朗明朗</span>
                  </div>
                </div>
                <blockquote>
                  “天意怜幽草，人间重晚晴”
                  <cite>—— 唐 · 李商隐《晚晴》</cite>
                </blockquote>
              </aside>
            )}
            {analysis && (
              <section className="meaning-result">
                <h2>{analysis.fullName}</h2>
                <p>
                  姓氏：{analysis.surname} · 名字：{analysis.givenName}
                </p>
                <div className="character-grid">
                  {analysis.characters.map((item) => (
                    <div key={item.character}>
                      <b>{item.character}</b>
                      <span>{item.meaningZh}</span>
                    </div>
                  ))}
                </div>
                {analysis.source && (
                  <blockquote>
                    “{analysis.source.quote}”
                    <cite>
                      —— {analysis.source.dynasty} · {analysis.source.author}《
                      {analysis.source.title}》
                    </cite>
                  </blockquote>
                )}
                <p>{analysis.note}</p>
                <p>
                  {analysis.risks.length
                    ? `注意：${analysis.risks.join("；")}`
                    : "未发现明显的生僻或不宜用字风险。"}
                </p>
                <NameAnalysisEnhancement analysis={analysis} ai={ai} language="zh" />
              </section>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

function ResultCard({
  name,
  requestId,
  index,
}: {
  name: ResultName;
  requestId: string;
  index: number;
}) {
  const [favorite, setFavorite] = useState(false);
  const score = useCountUp(name.score, 1200);
  return (
    <article className="result-card" style={{ "--i": index } as CSSProperties}>
      <div className="score-ring">
        <strong>{score}</strong>
        <span>综合分</span>
      </div>
      <div className="result-heading">
        <div>
          <h3>{name.fullName}</h3>
          <p>{name.pinyin}</p>
        </div>
        <div className="card-actions">
          <button
            type="button"
            aria-label="收藏"
            className={favorite ? "heart active" : "heart"}
            onClick={async () => setFavorite(await toggleFavorite(requestId, name.fullName))}
          >
            <Heart />
          </button>
          <button
            type="button"
            className="heart"
            aria-label="下载分享卡片"
            onClick={() => downloadNameCard(name, "zh")}
          >
            <Download />
          </button>
        </div>
      </div>
      <p className="recommendation">{name.recommendation}</p>
      <div className="character-grid">
        {name.characters.map((item) => (
          <div key={item.character}>
            <b>{item.character}</b>
            <span>{item.meaning}</span>
          </div>
        ))}
      </div>
      <blockquote>
        “{name.source.quote}”
        <cite>
          —— {name.source.dynasty} · {name.source.author}《{name.source.title}》
        </cite>
      </blockquote>
      <details>
        <summary>查看完整分析</summary>
        <dl>
          <dt>整体寓意</dt>
          <dd>{name.overallMeaning}</dd>
          <dt>音形日用</dt>
          <dd>{name.soundAndForm}</dd>
          <dt>适用场景</dt>
          <dd>{name.usage}</dd>
          <dt>注意事项</dt>
          <dd>{name.risks.length ? name.risks.join("；") : "未发现明显风险"}</dd>
        </dl>
      </details>
    </article>
  );
}

function NamingForm({
  config,
  me,
  onRefreshMe,
  onPurchase,
}: {
  config: SiteConfigResponse | null;
  me: MeResponse | null;
  onRefreshMe: () => Promise<void>;
  onPurchase: () => void;
}) {
  const [token, setToken] = useState("");
  const [task, setTask] = useState<GenerationRequest | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [workbenchRef, workbenchInView] = useInView<HTMLElement>();
  const onToken = useCallback((value: string) => setToken(value), []);

  useEffect(() => {
    const resultId = new URLSearchParams(window.location.search).get("result");
    if (resultId && me?.user) {
      void loadGeneration(resultId)
        .then(setTask)
        .catch(() => setError("无法读取这条历史记录"));
    }
  }, [me?.user]);

  async function poll(requestId: string) {
    for (let count = 0; count < 90; count += 1) {
      const current = await loadGeneration(requestId);
      setTask(current);
      if (current.status === "completed" || current.status === "failed_refunded") {
        await onRefreshMe();
        return current;
      }
      await new Promise((resolve) => window.setTimeout(resolve, 2000));
    }
    throw new Error("生成仍在后台进行，请稍后在账户记录中查看");
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    if (!me?.user) {
      setError("请先登录，再提交取名需求。");
      return;
    }
    if (me.credits < 1) {
      onPurchase();
      return;
    }
    const form = new FormData(event.currentTarget);
    const requiredCharacters = String(form.get("requiredCharacters") ?? "")
      .split(/[，,\s]+/)
      .filter(Boolean);
    const avoidedCharacters = String(form.get("avoidedCharacters") ?? "")
      .split(/[，,\s]+/)
      .filter(Boolean);
    const input = {
      surname: String(form.get("surname") ?? ""),
      gender: String(form.get("gender") ?? "any"),
      givenNameLength: Number(form.get("givenNameLength") ?? 2),
      style: String(form.get("style") ?? "classic"),
      source: String(form.get("source") ?? "any"),
      wishes: String(form.get("wishes") ?? ""),
      requiredCharacters,
      avoidedCharacters,
      useBazi: form.get("useBazi") === "on",
      birthDate: String(form.get("birthDate") ?? "") || undefined,
      birthTime: String(form.get("birthTime") ?? "") || undefined,
      birthRegion: String(form.get("birthRegion") ?? "") || undefined,
    };
    setBusy(true);
    try {
      trackEvent("naming_started", { site: "cn", mode: "new" });
      const requestId = await createGeneration(input, token);
      setTask({
        id: requestId,
        site: "cn",
        status: "queued",
        errorCode: null,
        errorMessage: null,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        result: null,
      });
      await onRefreshMe();
      const completed = await poll(requestId);
      if (completed.status === "completed")
        trackEvent("naming_completed", { site: "cn", mode: "new" });
    } catch (reason) {
      if (reason instanceof ApiRequestError && reason.status === 402) {
        onPurchase();
        return;
      }
      setError(reason instanceof Error ? reason.message : "提交失败");
    } finally {
      setBusy(false);
    }
  }

  async function regenerateInDirection() {
    if (!task) return;
    if ((me?.credits ?? 0) < 1) {
      onPurchase();
      return;
    }
    setBusy(true);
    setError("");
    try {
      trackEvent("naming_started", { site: "cn", mode: "regenerate" });
      const requestId = await regenerate(task.id, "保持本轮整体气质，但提供五个完全不同的候选");
      setTask({
        id: requestId,
        site: "cn",
        status: "queued",
        errorCode: null,
        errorMessage: null,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        result: null,
      });
      await onRefreshMe();
      const completed = await poll(requestId);
      if (completed.status === "completed")
        trackEvent("naming_completed", { site: "cn", mode: "regenerate" });
    } catch (reason) {
      if (reason instanceof ApiRequestError && reason.status === 402) {
        onPurchase();
        return;
      }
      setError(reason instanceof Error ? reason.message : "重新生成失败");
    } finally {
      setBusy(false);
    }
  }

  if (task?.status === "completed" && task.result) {
    return (
      <section className="results">
        <div className="results-header">
          <span className="eyebrow">本轮精选</span>
          <h2>五个名字，五种相遇</h2>
          <p>{task.result.summary}</p>
          <div className="result-actions">
            <button className="secondary-button" type="button" onClick={() => setTask(null)}>
              <RefreshCw />
              重新填写需求
            </button>
            <button
              className="primary-button"
              type="button"
              disabled={busy}
              onClick={regenerateInDirection}
            >
              沿此方向再生成 · 1 次
            </button>
          </div>
        </div>
        <div className="results-grid">
          {task.result.names.map((name, index) => (
            <ResultCard key={name.fullName} name={name} requestId={task.id} index={index} />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="naming-workbench" id="naming" ref={workbenchRef}>
      <div
        className={workbenchInView ? "workbench-intro reveal in-view" : "workbench-intro reveal"}
      >
        <span className="eyebrow">单次 ¥9.90 · 十次 ¥89 · 每次五个候选</span>
        <h2>
          先说清期待，
          <br />
          再慢慢找到名字。
        </h2>
        <p>我们先用规则引擎筛掉不自然的组合，再让 DeepSeek 理解你的寄语。失败不扣次数。</p>
        <div className="credit-chip">
          <ShoppingBag size={17} />
          当前次数 <strong>{me?.credits ?? 0}</strong>
        </div>
        {me?.user ? (
          <button
            className="secondary-button"
            type="button"
            disabled={!config?.features.payment}
            onClick={onPurchase}
          >
            {config?.features.payment ? "购买取名次数" : "支付即将开放"}
          </button>
        ) : (
          <AuthPanel language="zh" />
        )}
      </div>
      <form
        className={[
          "naming-form",
          workbenchInView ? "reveal in-view" : "reveal",
          task && task.status !== "failed_refunded" ? "thinking" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onSubmit={submit}
      >
        <div className="form-row">
          <label>
            姓氏
            <input name="surname" required maxLength={2} placeholder="如：林、欧阳" />
          </label>
          <label>
            性别倾向
            <select name="gender" defaultValue="any">
              <option value="any">不限</option>
              <option value="male">男</option>
              <option value="female">女</option>
              <option value="neutral">中性</option>
            </select>
          </label>
        </div>
        <div className="form-row">
          <label>
            名字字数
            <select name="givenNameLength" defaultValue="2">
              <option value="2">双字名</option>
              <option value="1">单字名</option>
            </select>
          </label>
          <label>
            主要气质
            <select name="style" defaultValue="classic">
              <option value="classic">古典耐看</option>
              <option value="modern">现代清爽</option>
              <option value="gentle">温润柔和</option>
              <option value="bright">明朗有朝气</option>
              <option value="grand">大气开阔</option>
              <option value="scholarly">书卷气</option>
              <option value="neutral">中性克制</option>
            </select>
          </label>
        </div>
        <label>
          偏好的典籍
          <select name="source" defaultValue="any">
            <option value="any">不限，综合筛选</option>
            <option value="shijing">诗经</option>
            <option value="chuci">楚辞</option>
            <option value="lunyu">论语</option>
            <option value="zhouyi">周易</option>
            <option value="tang">唐诗</option>
            <option value="song">宋词</option>
          </select>
        </label>
        <label>
          家庭寄语与期待
          <textarea
            name="wishes"
            rows={4}
            maxLength={500}
            placeholder="希望孩子清醒、温暖，有好奇心，也能自在地走自己的路……"
          />
        </label>
        <label className="bazi-option">
          <input type="checkbox" name="useBazi" />
          <span>加入八字五行轻量参考（非确定性命理结论）</span>
        </label>
        <div className="form-row bazi-fields">
          <label>
            出生日期
            <input name="birthDate" type="date" />
          </label>
          <label>
            出生时间
            <input name="birthTime" type="time" />
          </label>
        </div>
        <label>
          出生地区
          <input
            name="birthRegion"
            maxLength={80}
            placeholder="如：上海（用于说明时区与真太阳时边界）"
          />
        </label>
        <div className="form-row">
          <label>
            必须包含的字
            <input name="requiredCharacters" placeholder="如：知、宁（可留空）" />
          </label>
          <label>
            必须避开的字
            <input name="avoidedCharacters" placeholder="如：伟、强（可留空）" />
          </label>
        </div>
        <TurnstileWidget siteKey={config?.features.turnstileSiteKey ?? ""} onToken={onToken} />
        {error && (
          <p className="form-error">
            <CircleAlert />
            {error}
          </p>
        )}
        {task && task.status !== "failed_refunded" && (
          <div className="task-state" role="status">
            <span className="task-dots" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
            <span key={task.status} className="task-text">
              {task.status === "queued"
                ? "需求已入队，正在准备候选…"
                : task.status === "retrying"
                  ? "AI 繁忙，正在自动重试…"
                  : "正在理解寄语并撰写解释…"}
            </span>
          </div>
        )}
        {task?.status === "failed_refunded" && (
          <p className="form-error">
            <CircleAlert />
            {task.errorMessage ?? "生成失败，本次使用权已返还"}
          </p>
        )}
        <button className="primary-button generate-button" type="submit" disabled={busy}>
          {busy ? (
            <>
              <LoaderCircle className="spin" />
              正在取名
            </>
          ) : (
            <>
              <Feather />
              生成五个名字
            </>
          )}
        </button>
        <small className="consent">提交即表示同意服务条款。姓名建议仅供文化与创意参考。</small>
      </form>
    </section>
  );
}

function AccountPage() {
  return (
    <>
      <Header />
      <main>
        <AccountCenter language="zh" />
      </main>
      <Footer />
    </>
  );
}

export function AdminPage() {
  const [data, setData] = useState<{
    users: number;
    paidOrders: number;
    completed: number;
    failedRefunded: number;
  } | null>(null);
  const [reports, setReports] = useState<
    Array<{
      id: string;
      site: "cn" | "en";
      reporter_email: string;
      category: string;
      description: string;
      status: string;
      created_at: number;
    }>
  >([]);
  const [error, setError] = useState("");
  const [adjustmentMessage, setAdjustmentMessage] = useState("");
  useEffect(() => {
    void fetch("/api/admin/overview")
      .then(async (response) => {
        const body = (await response.json()) as {
          users?: number;
          paidOrders?: number;
          completed?: number;
          failedRefunded?: number;
          error?: string;
        };
        if (!response.ok) throw new Error(body.error ?? "无权访问管理概览");
        setData({
          users: body.users ?? 0,
          paidOrders: body.paidOrders ?? 0,
          completed: body.completed ?? 0,
          failedRefunded: body.failedRefunded ?? 0,
        });
      })
      .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "加载失败"));
    void fetch("/api/admin/compliance-reports")
      .then(async (response) => {
        const body = (await response.json()) as { reports?: typeof reports; error?: string };
        if (!response.ok) throw new Error(body.error ?? "无权访问举报列表");
        setReports(body.reports ?? []);
      })
      .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "加载失败"));
  }, []);
  async function adjust(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const response = await fetch("/api/admin/credits/adjust", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: String(form.get("email") ?? ""),
        site: String(form.get("site") ?? "cn"),
        delta: Number(form.get("delta") ?? 0),
        note: String(form.get("note") ?? ""),
      }),
    });
    const body = (await response.json()) as { error?: string; available?: number };
    setAdjustmentMessage(
      response.ok ? `调整成功，当前次数 ${body.available ?? 0}` : (body.error ?? "调整失败"),
    );
  }
  async function updateReport(id: string, status: "reviewing" | "resolved" | "rejected") {
    const response = await fetch(`/api/admin/compliance-reports/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status, resolutionNote: "运营台人工复核" }),
    });
    if (!response.ok) {
      const body = (await response.json()) as { error?: string };
      setError(body.error ?? "更新举报状态失败");
      return;
    }
    setReports((items) => items.map((item) => (item.id === id ? { ...item, status } : item)));
  }
  return (
    <>
      <Header />
      <main className="account-page">
        <span className="eyebrow">OPERATIONS</span>
        <h1>运营概览</h1>
        {error && <p className="form-error">{error}</p>}
        <div className="admin-grid">
          <div>
            <span>注册用户</span>
            <strong>{data?.users ?? "—"}</strong>
          </div>
          <div>
            <span>已支付订单</span>
            <strong>{data?.paidOrders ?? "—"}</strong>
          </div>
          <div>
            <span>成功生成</span>
            <strong>{data?.completed ?? "—"}</strong>
          </div>
          <div>
            <span>失败返还</span>
            <strong>{data?.failedRefunded ?? "—"}</strong>
          </div>
        </div>
        <p className="admin-note">
          管理员权限由 Worker Secret `ADMIN_EMAILS` 控制。详细订单、AI 调用与审计记录保存在
          D1，可通过 Cloudflare Dashboard 或 Wrangler 查询。
        </p>
        <section className="admin-adjust">
          <h2>调整用户次数</h2>
          <form onSubmit={adjust}>
            <label>
              用户邮箱
              <input name="email" type="email" required />
            </label>
            <label>
              站点
              <select name="site">
                <option value="cn">中文站</option>
                <option value="en">英文站</option>
              </select>
            </label>
            <label>
              增减数量
              <input name="delta" type="number" min={-1000} max={1000} required />
            </label>
            <label>
              操作原因
              <input name="note" minLength={2} maxLength={300} required />
            </label>
            <button className="primary-button" type="submit">
              确认调整
            </button>
          </form>
          {adjustmentMessage && <p>{adjustmentMessage}</p>}
        </section>
        <section className="admin-reports">
          <h2>内容安全与用户举报</h2>
          {reports.length === 0 ? (
            <p>暂无举报。</p>
          ) : (
            reports.map((report) => (
              <article key={report.id}>
                <div>
                  <strong>{report.category}</strong>
                  <span>{report.status}</span>
                  <small>
                    {report.site.toUpperCase()} · {report.reporter_email} ·{" "}
                    {new Date(report.created_at).toLocaleString("zh-CN")}
                  </small>
                </div>
                <p>{report.description}</p>
                <div className="report-actions">
                  <button type="button" onClick={() => updateReport(report.id, "reviewing")}>
                    标记复核中
                  </button>
                  <button type="button" onClick={() => updateReport(report.id, "resolved")}>
                    已处理
                  </button>
                  <button type="button" onClick={() => updateReport(report.id, "rejected")}>
                    驳回
                  </button>
                </div>
              </article>
            ))
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}

function HomePage() {
  const [config, setConfig] = useState<SiteConfigResponse | null>(null);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const refreshMe = useCallback(async () => setMe(await loadMe()), []);
  const openPurchase = useCallback(() => setPurchaseOpen(true), []);
  const closePurchase = useCallback(() => setPurchaseOpen(false), []);
  const heroArtRef = usePointerParallax<HTMLDivElement>();
  const [methodRef, methodInView] = useInView<HTMLElement>();
  const [manifestoRef, manifestoInView] = useInView<HTMLElement>();
  useEffect(() => {
    void loadSiteConfig().then(setConfig);
    void refreshMe();
  }, [refreshMe]);
  useEffect(() => {
    if ((me?.credits ?? 0) > 0) setPurchaseOpen(false);
  }, [me?.credits]);
  return (
    <>
      <Header />
      <main>
        <section className="hero">
          <div className="hero-copy">
            <span className="eyebrow">诗词有出处 · 名字能日用</span>
            <h1>
              取一个
              <br />
              <i>能用一生</i>的名字。
            </h1>
            <p>
              不堆砌生僻字，也不把命运交给分数。我们从真实典籍、音律与日常使用出发，再让 DeepSeek
              理解你的期待。
            </p>
            <a className="primary-button" href="#naming">
              开始认真取名
              <ArrowRight />
            </a>
            <div className="hero-proof">
              <span>
                <ShieldCheck />
                硬规则先过滤
              </span>
              <span>
                <Sparkles />
                AI 再理解
              </span>
              <span>
                <Clock3 />
                失败自动返还
              </span>
            </div>
          </div>
          <div className="hero-art" ref={heroArtRef} role="img" aria-label="示例名字">
            <div className="moon" />
            <div className="name-slip back">
              <span>知许</span>
              <small>知性 · 含蓄</small>
            </div>
            <div className="name-slip front">
              <span>景明</span>
              <small>春和景明</small>
              <b>93</b>
            </div>
            <p>
              每一个候选
              <br />
              都有来处，也经得起称呼。
            </p>
          </div>
        </section>
        <NamingForm config={config} me={me} onRefreshMe={refreshMe} onPurchase={openPurchase} />
        <section className="method" id="method" ref={methodRef}>
          <div className={methodInView ? "reveal in-view" : "reveal"}>
            <span className="eyebrow">不是一次随机抽签</span>
            <h2>三重筛选，先排除“不能用”，再寻找“真正适合”。</h2>
          </div>
          <div
            className={
              methodInView ? "method-grid reveal-cascade in-view" : "method-grid reveal-cascade"
            }
          >
            <article>
              <b>01</b>
              <BookOpenText />
              <h3>有据</h3>
              <p>从诗经、楚辞、唐诗宋词等文本中检索真实原句，出处可以回查。</p>
            </article>
            <article>
              <b>02</b>
              <ShieldCheck />
              <h3>能用</h3>
              <p>检查多音、生僻、谐音、书写压力和长期使用场景，不让典故掩盖问题。</p>
            </article>
            <article>
              <b>03</b>
              <Sparkles />
              <h3>懂你</h3>
              <p>DeepSeek 阅读家庭寄语，从候选集中做个性化选择并解释，不凭空捏造出处。</p>
            </article>
          </div>
        </section>
        <section
          className={manifestoInView ? "manifesto reveal in-view" : "manifesto reveal"}
          ref={manifestoRef}
        >
          <ScrollText />
          <blockquote>
            “好名字不是玄学答案，
            <br />
            是声音、文字与期待达成的一次和解。”
          </blockquote>
          <p>一次生成五个完整方案，单次 ¥9.90，十次 ¥89。</p>
        </section>
      </main>
      <Footer />
      <PurchaseModal
        language="zh"
        open={purchaseOpen}
        products={config?.products ?? []}
        paymentEnabled={config?.features.payment ?? false}
        onClose={closePurchase}
        onRefresh={refreshMe}
      />
    </>
  );
}

export default function App() {
  const path = window.location.pathname;
  if (LEGAL_PATHS.has(path as LegalPath)) return <LegalPage path={path as LegalPath} />;
  if (path === "/report") return <ReportPage />;
  if (path === "/pricing") return <PricingPage />;
  if (path === "/login")
    return (
      <>
        <Header />
        <main>
          <AuthPage language="zh" />
        </main>
        <Footer />
      </>
    );
  if (path.startsWith("/figures/"))
    return <FigurePage name={decodeURIComponent(path.slice("/figures/".length))} />;
  if (path.startsWith("/surnames/"))
    return <SurnameDetailPage hanzi={decodeURIComponent(path.slice("/surnames/".length))} />;
  if (path === "/surnames") return <SurnamePage />;
  if (path === "/meaning") return <MeaningPage />;
  if (path === "/account") return <AccountPage />;
  if (path === "/reset-password")
    return (
      <>
        <Header />
        <main className="reset-page">
          <ResetPasswordPanel language="zh" />
        </main>
        <Footer />
      </>
    );
  return <HomePage />;
}
